import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getMetaData } from '../../../tool';
import AnswerView from './answer';
import AnswerHeader from './header';
import { Comment } from '../../../types/types';
import QuestionBody from './questionBody';
import VoteComponent from '../voteComponent';
import CommentSection from '../commentSection';
import useAnswerPage from '../../../hooks/useAnswerPage';
import { Button } from '../../ui/button';
import { Card, CardHeader, CardContent, CardTitle, CardFooter } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import Separator from '../../ui/separator';
import AnswerDialog from '../answerDialog';
import AskQuestionButton from '../askQuestionButton';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../common/BreadcrumbNav';
import {
  generateLLMSummary,
  generateAISolution,
  generateFollowUpResponse,
} from '../../../services/llmService';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Textarea } from '../../ui/textarea';

/**
 * AnswerPage component that displays the full content of a question along with its answers.
 * It also includes the functionality to vote, ask a new question, and post a new answer.
 */
const AnswerPage = () => {
  const { questionID, question, handleNewComment, loading, error } = useAnswerPage();

  // State for response summary
  const [responseSummary, setResponseSummary] = useState<string | null>(null);
  const [aiSolution, setAiSolution] = useState<string | null>(null);
  const [isGeneratingSolution, setIsGeneratingSolution] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [solutionError, setSolutionError] = useState<string>('');
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpResponse, setFollowUpResponse] = useState<string | null>(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  // AbortController for cleanup
  useEffect(() => {
    const controller = new AbortController();
    return () => controller.abort(); // Cleanup on unmount
  }, []);

  // Rendering loading state
  if (loading) {
    return (
      <div className='container py-8'>
        <Card>
          <CardContent className='flex justify-center p-8'>
            <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Rendering error state
  if (error) {
    return (
      <div className='container py-8'>
        <Card>
          <CardContent className='p-6'>
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If question data is not available
  if (!question) {
    return (
      <div className='container py-8'>
        <Card>
          <CardContent className='p-6'>
            <Alert variant='default' className='bg-amber-50 border-amber-200'>
              <AlertCircle className='h-4 w-4 text-amber-500' />
              <AlertDescription>Question not found or has been removed.</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  const generateSummary = async () => {
    if (question.answers.length > 0) {
      setIsGeneratingSummary(true);
      try {
        const summary = await generateLLMSummary(question);
        setResponseSummary(summary);
      } catch (err) {
        setResponseSummary('Error generating summary. Please try again.');
      } finally {
        setIsGeneratingSummary(false);
      }
    } else {
      setResponseSummary('No answers available to summarize.');
    }
  };

  const handleGenerateAISolution = async () => {
    if (!question?.text) {
      setSolutionError('Question details not available');
      return;
    }

    setIsGeneratingSolution(true);
    setSolutionError('');
    try {
      const solution = await generateAISolution(question.text);
      setAiSolution(solution);
    } catch (err) {
      setSolutionError('Failed to generate AI solution. Please try again.');
    } finally {
      setIsGeneratingSolution(false);
    }
  };

  const handleFollowUp = async () => {
    if (!aiSolution) {
      setSolutionError('Please generate an AI solution first');
      return;
    }
    if (!followUpQuestion.trim()) {
      setSolutionError('Please enter a follow-up question');
      return;
    }

    setIsGeneratingFollowUp(true);
    try {
      const response = await generateFollowUpResponse(question.title, aiSolution, followUpQuestion);
      setFollowUpResponse(response);
      setFollowUpOpen(false);
      setFollowUpQuestion('');
    } catch (err) {
      setSolutionError('Failed to generate follow-up response');
    } finally {
      setIsGeneratingFollowUp(false);
    }
  };

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Questions', href: '/home' },
    { label: question.title },
  ];

  return (
    <div className='container py-8'>
      <div className='flex flex-col gap-4'>
        {/* Breadcrumb Navigation */}
        <BreadcrumbNav items={breadcrumbItems} />

        <div className='flex justify-end'>
          <AskQuestionButton />
        </div>
        <Card>
          <CardHeader>
            <CardTitle className='text-2xl'>{question.title}</CardTitle>
            <AnswerHeader ansCount={question.answers.length} />
            <VoteComponent question={question} />
          </CardHeader>

          <CardContent className='space-y-6'>
            <QuestionBody
              views={question.views.length}
              text={question.text}
              askby={question.askedBy}
              meta={getMetaData(new Date(question.askDateTime))}
            />

            <CommentSection
              comments={question.comments}
              handleAddComment={(comment: Comment) =>
                handleNewComment(comment, 'question', questionID)
              }
            />

            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>
                {question.answers.length} Answer{question.answers.length !== 1 ? 's' : ''}
              </h3>

              {question.answers.length > 0 ? (
                <div className='space-y-4'>
                  {question.answers.map((a, index) => (
                    <React.Fragment key={String(a._id)}>
                      <AnswerView
                        text={a.text}
                        ansBy={a.ansBy}
                        meta={getMetaData(new Date(a.ansDateTime))}
                        comments={a.comments}
                        handleAddComment={(comment: Comment) =>
                          handleNewComment(comment, 'answer', String(a._id))
                        }
                      />
                      {index < question.answers.length - 1 && <Separator />}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <Alert variant='default' className='bg-amber-50 border-amber-200'>
                  <AlertCircle className='h-4 w-4 text-amber-500' />
                  <AlertDescription>
                    There are no answers yet. Be the first to answer!
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Response Summary Section */}
            <div className='p-4 border border-gray-200 rounded-md bg-gray-50'>
              <h3 className='text-lg font-semibold mb-3'>Response Summary</h3>
              <Button
                onClick={generateSummary}
                variant='outline'
                className='mb-3'
                disabled={isGeneratingSummary}>
                {isGeneratingSummary ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Generating...
                  </>
                ) : (
                  'Generate Summary'
                )}
              </Button>

              {responseSummary && (
                <div className='p-4 bg-blue-50 border border-blue-200 rounded'>
                  <div className='prose prose-sm max-w-none'>
                    <ReactMarkdown>{responseSummary}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            {/* AI Solution Section */}
            <div className='p-4 border border-gray-200 rounded-md bg-gray-50'>
              <div className='flex justify-between items-center mb-3'>
                <h3 className='text-lg font-semibold'>AI Solution Preview</h3>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    onClick={handleGenerateAISolution}
                    disabled={isGeneratingSolution}>
                    {isGeneratingSolution ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Generating...
                      </>
                    ) : (
                      'Generate AI Solution'
                    )}
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => setFollowUpOpen(true)}
                    disabled={!aiSolution}>
                    Follow Up
                  </Button>
                </div>
              </div>

              {solutionError && (
                <Alert variant='destructive' className='mb-3'>
                  <AlertCircle className='h-4 w-4' />
                  <AlertDescription>{solutionError}</AlertDescription>
                </Alert>
              )}

              {aiSolution && (
                <div className='p-4 bg-blue-50 border border-blue-200 rounded max-h-[200px] overflow-y-auto'>
                  <div className='prose prose-sm max-w-none'>
                    <ReactMarkdown>{aiSolution}</ReactMarkdown>
                  </div>
                </div>
              )}

              {followUpResponse && (
                <div className='mt-4 p-4 bg-green-50 border border-green-200 rounded max-h-[200px] overflow-y-auto'>
                  <h4 className='font-semibold mb-2'>Follow-up Response:</h4>
                  <div className='prose prose-sm max-w-none'>
                    <ReactMarkdown>{followUpResponse}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </CardContent>

          <CardFooter className='flex justify-end pb-6'>
            <AnswerDialog questionID={questionID} questionTitle={question.title} />
          </CardFooter>
        </Card>
      </div>

      <Dialog open={followUpOpen} onOpenChange={setFollowUpOpen}>
        <DialogContent className='sm:max-w-[550px]'>
          <DialogHeader>
            <DialogTitle>Ask a Follow-up Question</DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <Textarea
              placeholder='Type your follow-up question here...'
              value={followUpQuestion}
              onChange={e => setFollowUpQuestion(e.target.value)}
              className='min-h-[100px]'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setFollowUpOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleFollowUp}
              disabled={isGeneratingFollowUp || !followUpQuestion.trim()}>
              {isGeneratingFollowUp ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Generating...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnswerPage;
